import React, { useCallback, useEffect, useRef, useState } from "react";
import Layout from "@/components/Layout";
import { useAuth, API } from "@/contexts/AuthContext";
import axios from "axios";
import { toast } from "sonner";
import { Check, Crown, Zap, Calendar } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useSearchParams } from "react-router-dom";
import { usePlans } from "@/hooks/usePlans";
import { isOwner as hasOwnerRole } from "@/lib/permissions";
import { LEITURA } from "../lib/larguras";

export const navigateToPortal = (url) => {
  if (typeof window !== "undefined" && typeof window.__portalNavigate === "function") return window.__portalNavigate(url);
  window.location.href = url;
};

// Esqueleto mostrado enquanto /api/plans responde. Sem preço de propósito:
// esta é a tela onde a pessoa decide pagar, e um valor de reserva aqui pisca
// um número que não é o que o Stripe vai cobrar.
const PLANS_FALLBACK = {
  pro_monthly: {
    label: "PRO Mensal",
    period: "/mês",
    highlight: "Oferta de lançamento",
    features: [
      "Processos ilimitados",
      "Usuários ilimitados",
      "Até 10 OABs cadastradas",
      "Intimações automáticas (a cada 30 min)",
      "Processos atualizados 3× ao dia",
      "Notas por processo",
      "Suporte prioritário",
      "50 pesquisas de jurisprudências por mês",
    ],
  },
  pro_annual: {
    label: "PRO Anual",
    period: "/ano",
    highlight: "Oferta de lançamento · 2 meses grátis",
    features: [
      "Processos ilimitados",
      "Usuários ilimitados",
      "Até 10 OABs cadastradas",
      "Intimações automáticas (a cada 30 min)",
      "Processos atualizados 3× ao dia",
      "Notas por processo",
      "Suporte prioritário",
      "50 pesquisas de jurisprudências por mês",
      "2 meses de desconto",
    ],
  },
};

export default function PlanoPage() {
  const { user, checkAuth } = useAuth();
  const isOwner = hasOwnerRole(user);
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checkoutLoading, setCheckoutLoading] = useState(null);
  const [searchParams] = useSearchParams();
  const [checkingPayment, setCheckingPayment] = useState(false);
  const { planPagePlans, getByKey, loading: plansLoading } = usePlans();

  const sessionId = searchParams.get("session_id");

  const loadSubscription = useCallback(async () => {
    try {
      if (isOwner) {
        const res = await axios.get(`${API}/payments/subscription`, { withCredentials: true });
        setSubscription(res.data);
      } else {
        // Members can read the workspace, but billing.read is owner-only.
        // Keep the real workspace plan without probing a protected billing route.
        const res = await axios.get(`${API}/auth/workspace`, { withCredentials: true });
        const workspace = res.data?.workspace || {};
        setSubscription({
          plan: workspace.plan || "free",
          plan_period: workspace.plan_period,
          subscription_status: workspace.subscription_status,
          plan_expires_at: workspace.plan_expires_at,
        });
      }
    } catch {}
    finally { setLoading(false); }
  }, [isOwner]);

  useEffect(() => {
    loadSubscription();
  }, [loadSubscription]);

  useEffect(() => {
    if (!sessionId || !isOwner) return;
    let active = true;
    let timer = null;
    setCheckingPayment(true);

    const poll = async (attempts = 0) => {
      if (!active) return;
      if (attempts >= 5) {
        if (active) {
          setCheckingPayment(false);
          toast.info("Verifique o status do pagamento em alguns minutos.");
        }
        return;
      }
      try {
        const res = await axios.get(`${API}/payments/status/${sessionId}`, { withCredentials: true });
        if (!active) return;
        if (res.data.payment_status === "paid") {
          setCheckingPayment(false);
          toast.success("Pagamento confirmado! Plano PRO ativado.");
          loadSubscription();
          // Sem isso o contexto continua com o plano antigo e a faixa de
          // "upgrade do FREE" acompanha o assinante até ele recarregar a página.
          checkAuth();
        } else if (res.data.status === "expired") {
          setCheckingPayment(false);
          toast.error("Sessão de pagamento expirada.");
        } else {
          timer = setTimeout(() => poll(attempts + 1), 2000);
        }
      } catch {
        if (active) timer = setTimeout(() => poll(attempts + 1), 2000);
      }
    };

    poll();
    return () => {
      active = false;
      if (timer) clearTimeout(timer);
    };
  }, [sessionId, isOwner, loadSubscription, checkAuth]);

  const [portalLoading, setPortalLoading] = useState(false);

  const handlePortal = async (event) => {
    event?.preventDefault();
    setPortalLoading(true);
    try {
      const res = await axios.post(`${API}/payments/portal`, {}, { withCredentials: true });
      navigateToPortal(res.data.url);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (detail?.code === "stripe_customer_missing") {
        toast.error(detail.message);
        await loadSubscription();
      } else {
        toast.error(typeof detail === "string" ? detail : "Erro ao abrir portal de assinatura");
      }
      setPortalLoading(false);
    }
  };

  const handleCheckout = async (plan) => {
    setCheckoutLoading(plan);
    try {
      const res = await axios.post(
        `${API}/payments/checkout`,
        { plan, origin_url: window.location.origin },
        { withCredentials: true }
      );
      window.location.href = res.data.url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao criar checkout");
    } finally {
      setCheckoutLoading(null);
    }
  };

  const PLANS = planPagePlans(subscription?.plan) ?? (plansLoading ? PLANS_FALLBACK : {});

  // A comparação mostrava "FREE" numa coluna fixa, mesmo depois de o plano
  // sair de venda. START e FREE têm os mesmos limites, então uma coluna serve
  // às duas — mas o rótulo tem de ser o do plano que existe hoje.
  const candidatosDeEntrada = [getByKey("start"), getByKey("free")].filter(Boolean);
  const planoDeEntrada =
    candidatosDeEntrada.find((p) => p.active !== false) || candidatosDeEntrada[0];
  const rotuloDaEntrada = planoDeEntrada?.label || "START";

  const isPro = subscription?.plan === "pro";
  // O START é assinatura paga, com a mesma cota do FREE. Tratá-lo como "o que
  // não é PRO" mostrava FREE para quem acabou de pagar — e, pior, escondia o
  // botão de gerenciar a assinatura, deixando o assinante sem como cancelar.
  const isStart = subscription?.plan === "start";
  const isPago = isPro || isStart;
  const rotuloDoPlano = isPro ? "PRO" : isStart ? "START" : "FREE";

  // Vindo do cadastro pelo botão de assinar: abre o checkout direto, para o
  // usuário não ter de reencontrar o plano que acabou de escolher.
  const requestedCheckout = searchParams.get("checkout");
  const autoCheckoutDone = useRef(false);
  useEffect(() => {
    if (autoCheckoutDone.current || loading || !requestedCheckout) return;
    if (!isOwner || isPro || !PLANS[requestedCheckout]) return;
    autoCheckoutDone.current = true;
    handleCheckout(requestedCheckout);
    // handleCheckout redireciona para o Stripe; deps extras causariam recompra.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, requestedCheckout, isOwner, isPro, PLANS]);

  return (
    <Layout>
      <div className={LEITURA}>
        {/* Header */}
        <div className="animate-fade-in-1 mb-8">
          <p className="text-[#335480] text-sm uppercase tracking-widest font-medium mb-1">Assinatura</p>
          <h1 className="text-4xl font-bold text-[#001533]" style={{ fontFamily: "Cabinet Grotesk" }}>
            Plano
          </h1>
        </div>

        {/* Checking payment status */}
        {checkingPayment && (
          <div className="bg-[#e5edf5] border border-[#003882]/20 rounded-lg p-5 mb-6 flex items-center gap-3 animate-fade-in-1">
            <div className="w-5 h-5 border-2 border-[#003882] border-t-transparent rounded-full animate-spin flex-shrink-0" />
            <p className="text-[#001533] font-medium">Confirmando pagamento...</p>
          </div>
        )}

        {/* Current Plan */}
        <div className="bg-white border border-[#D1DEEE] rounded-lg p-6 mb-8 animate-fade-in-2">
          <h2 className="text-xl font-bold text-[#001533] mb-4" style={{ fontFamily: "Cabinet Grotesk" }}>
            Plano Atual
          </h2>
          {loading ? (
            <div className="h-12 bg-[#F4F7FB] rounded animate-pulse" />
          ) : (
            <div className="flex items-center justify-between">
              <div>
                <div className="flex items-center gap-3 mb-2">
                  <Crown size={20} className={isPago ? "text-[#003882]" : "text-[#335480]"} />
                  <span className={`text-2xl font-bold ${isPago ? "text-[#003882]" : "text-[#001533]"}`} style={{ fontFamily: "Cabinet Grotesk" }} data-testid="plano-atual">
                    {rotuloDoPlano}
                  </span>
                  {isPago && (
                    <span className="text-xs px-2 py-0.5 bg-[#003882] text-white rounded-full font-bold">
                      {subscription?.plan_period === "annual" ? "Anual" : "Mensal"}
                    </span>
                  )}
                </div>
                {isPago && subscription?.plan_expires_at && (() => {
                  const d = new Date(subscription.plan_expires_at);
                  if (isNaN(d.getTime())) return null;
                  return (
                    <div className="flex items-center gap-2 text-[#335480] text-sm">
                      <Calendar size={14} />
                      {subscription?.subscription_status === "active" ? "Renova em" : "Válido até"}: {d.toLocaleDateString("pt-BR")}
                    </div>
                  );
                })()}
                {!isPro && (
                  <p className="text-[#335480] text-base">
                    100 processos • 2 usuários • 1 OAB
                  </p>
                )}
              </div>
              {isPago && (
                <span className="text-sm px-4 py-2 bg-[#e5edf5] text-[#003882] rounded-full font-semibold">
                  Ativo
                </span>
              )}
            </div>
          )}
        </div>

        {/* Upgrade Plans */}
        {!isPro && isOwner && (
          <>
            <h2 className="text-2xl font-bold text-[#001533] mb-6 animate-fade-in-2" style={{ fontFamily: "Cabinet Grotesk" }}>
              {isStart ? "Fazer upgrade para PRO" : "Escolha seu plano"}
            </h2>
            {/* items-start: sem isso o grid estica todos os cartões à altura
                do maior, deixando um vazio enorme sob os menores. */}
            <div className={`grid gap-6 items-start animate-fade-in-3 ${Object.keys(PLANS).length > 2 ? "md:grid-cols-3" : "md:grid-cols-2"}`}>
              {Object.entries(PLANS).map(([planKey, plan]) => (
                <div
                  key={planKey}
                  data-testid={`plan-card-${planKey}`}
                  className={`rounded-xl p-8 border flex flex-col ${
                    planKey === "pro_annual"
                      ? "bg-[#003882] border-[#003882]"
                      : "bg-white border-[#D1DEEE]"
                  }`}
                >
                  {plan.highlight && (
                    // O selo era branco sobre branco em qualquer card que não
                    // fosse o azul do anual — invisível.
                    <div className={`inline-block text-xs font-bold px-3 py-1 rounded-full mb-4 ${
                      planKey === "pro_annual"
                        ? "bg-white/20 text-white"
                        : "bg-[#e5edf5] text-[#003882]"
                    }`}>
                      {plan.highlight}
                    </div>
                  )}
                  <h3
                    className={`text-xl font-bold mb-2 ${planKey === "pro_annual" ? "text-white" : "text-[#001533]"}`}
                    style={{ fontFamily: "Cabinet Grotesk" }}
                  >
                    {plan.label}
                  </h3>
                  {/* flex-wrap: um valor anual longo estourava a largura do
                      cartão e aparecia cortado. */}
                  <div className="mb-6 flex flex-wrap items-baseline gap-x-2">
                    {plan.oldPrice && (
                      <span className={`text-lg line-through mr-2 ${planKey === "pro_annual" ? "text-blue-300/70" : "text-[#335480]/60"}`}>
                        {plan.oldPrice}
                      </span>
                    )}
                    <span className={`text-3xl lg:text-4xl font-bold ${planKey === "pro_annual" ? "text-white" : "text-[#003882]"}`} style={{ fontFamily: "Cabinet Grotesk" }}>
                      {plan.price || (
                        <span
                          data-testid="preco-carregando"
                          className={`inline-block h-8 w-28 rounded animate-pulse align-middle ${planKey === "pro_annual" ? "bg-white/20" : "bg-[#D1DEEE]"}`}
                        />
                      )}
                    </span>
                    <span className={`text-lg ${planKey === "pro_annual" ? "text-blue-200" : "text-[#335480]"}`}>
                      {plan.period}
                    </span>
                  </div>
                  <ul className="space-y-3 mb-8 flex-1">
                    {plan.features.map((f, i) => (
                      <li key={i} className={`flex items-center gap-3 text-base ${planKey === "pro_annual" ? "text-blue-100" : "text-[#335480]"}`}>
                        <Check size={16} className={planKey === "pro_annual" ? "text-blue-300" : "text-[#003882]"} />
                        {f}
                      </li>
                    ))}
                  </ul>
                  <Button
                    data-testid={`checkout-${planKey}-btn`}
                    onClick={() => handleCheckout(planKey)}
                    disabled={!!checkoutLoading}
                    className={`w-full h-12 font-bold text-base ${
                      planKey === "pro_annual"
                        ? "bg-white text-[#003882] hover:bg-blue-50"
                        : "bg-[#003882] hover:bg-[#00285e] text-white"
                    }`}
                  >
                    {checkoutLoading === planKey ? (
                      <span className="flex items-center gap-2">
                        <span className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                        Aguarde...
                      </span>
                    ) : (
                      <>
                        <Zap size={18} />
                        Assinar {plan.label}
                      </>
                    )}
                  </Button>
                </div>
              ))}
            </div>

            {/* Feature comparison */}
            <div className="mt-8 bg-white border border-[#D1DEEE] rounded-lg p-6 animate-fade-in-4">
              <h3 className="text-lg font-bold text-[#001533] mb-4" style={{ fontFamily: "Cabinet Grotesk" }}>
                Comparação de planos
              </h3>
              <table className="w-full text-base">
                <thead>
                  <tr className="border-b border-[#D1DEEE]">
                    <th className="text-left py-3 text-[#335480] font-semibold">Recurso</th>
                    <th className="text-center py-3 text-[#335480] font-semibold">{rotuloDaEntrada}</th>
                    <th className="text-center py-3 text-[#003882] font-semibold">PRO</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#D1DEEE]">
                  {[
                    ["Processos", "Até 100", "Ilimitados"],
                    ["Usuários", "2", "Ilimitados"],
                    ["OABs", "1", "10"],
                    ["Intimações do DJEN", "A cada 30 min", "A cada 30 min"],
                    ["Atualização de processos", "1× ao dia", "3× ao dia"],
                    ["Jurisprudências", "5 no total", "50 por mês"],
                    ["Agenda jurídica", "✓", "✓"],
                    ["Notas por processo", "✓", "✓"],
                    ["Kanban de tarefas", "1 quadro", "Múltiplos quadros"],
                    ["Alertas por e-mail e Telegram", "✓", "✓"],
                    ["Clientes e contatos", "✓", "✓"],
                    ["Finanças do escritório", "—", "✓"],
                    ["Suporte prioritário", "—", "✓"],
                  ].map(([rec, free, pro], i) => (
                    <tr key={i} className="hover:bg-[#F4F7FB]">
                      <td className="py-3 text-[#001533] font-medium">{rec}</td>
                      <td className="py-3 text-center text-[#335480]">{free}</td>
                      <td className="py-3 text-center text-[#003882] font-semibold">{pro}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}

        {isPago && isOwner && (
          <div className="bg-[#e5edf5] border border-[#003882]/20 rounded-xl p-8 text-center animate-fade-in-3">
            <Crown size={40} className="mx-auto mb-4 text-[#003882]" />
            <h3 className="text-2xl font-bold text-[#001533] mb-2" style={{ fontFamily: "Cabinet Grotesk" }}>
              Você está no Plano {rotuloDoPlano}
            </h3>
            <p className="text-[#335480] text-lg mb-6">
              {isPro
                ? "Aproveite processos ilimitados, múltiplas OABs e todos os recursos da plataforma."
                : "Sua assinatura está ativa. Aqui você altera a forma de pagamento, baixa recibos ou cancela quando quiser."}
            </p>
            <Button
              type="button"
              data-testid="manage-subscription-btn"
              onClick={handlePortal}
              disabled={portalLoading}
              variant="outline"
              className="border-[#003882] text-[#003882] hover:bg-[#003882] hover:text-white font-semibold h-11 px-6"
            >
              {portalLoading ? (
                <span className="flex items-center gap-2">
                  <span className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                  Abrindo...
                </span>
              ) : (
                "Gerenciar assinatura"
              )}
            </Button>
          </div>
        )}
      </div>
    </Layout>
  );
}
